import { useEffect, useRef, useState } from 'react'
import { useRouteError } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { ErrorScreen } from './ErrorScreen.js'

/**
 * The data router catches a route's render error at its own boundary, which sits *below* the app's
 * `Sentry.ErrorBoundary` — so that boundary never sees it. `wrapCreateBrowserRouterV7` instruments
 * tracing only and captures nothing, which left every route-level crash reported to nobody. Hence the
 * explicit capture: without it the user gets a fallback and we get silence.
 *
 * Reporting is keyed on the error itself rather than on mount, because this component re-renders and a
 * crash counted twice inflates the one number these events are read for — how many users it hit.
 */
export const RouteErrorBoundary = () => {
  const error = useRouteError()
  const [eventId, setEventId] = useState<string>()
  const reportedFor = useRef<unknown>(undefined)

  useEffect(() => {
    if (reportedFor.current === error) return
    reportedFor.current = error
    setEventId(Sentry.captureException(error, { tags: { boundary: 'route' } }))
  }, [error])

  return <ErrorScreen eventId={eventId} />
}
